import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import SectionLabel from "../components/SectionLabel";
import { Button } from "../components/ui/button";

const VOL_IMG = "/vanny.jpg";

const TEAM = [
  { name: "Ganga Banke", role: "Founder & Head of Rescue", img: "/team/t1.jpg" },
  { name: "Bhawana Vishwakarma", role: "Care & Recovery Lead", img: "/team/t2.jpg" },
  { name: "Gulabi Labanna", role: "Sanctuary Manager", img: "/team/t3.jpg" },
  { name: "Manthan Banke", role: "Rescue & Operations", img: "/team/t4.jpg" },
];

const TIMELINE = [
  { year: "2019", title: "It started with a bowl of food", body: "During the coronavirus lockdown, we started feeding community dogs in our locality. Slowly, our feeding drive reached 100+ animals across the neighbourhood." },
  { year: "2021", title: "Our home became a shelter", body: "By 2021, our team already had several rescued cases healing inside our houses — sharing our beds, our floors, our space — living with us as family." },
  { year: "2023", title: "Vacate, repeat, vacate again", body: "We started moving into rented places to care for the growing pack, but were forced to vacate every few months. Five times, we packed up the dogs and started over." },
  { year: "2026", title: "One acre. One forever home.", body: "We have acquired a one-acre land and begun building a permanent cage-free sanctuary for the 300+ rescued dogs in our care. The dream finally has a foundation." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="pt-16 lg:pt-24 pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-7 fade-up">
            <SectionLabel>Our story</SectionLabel>
            <h1 className="font-serif text-5xl lg:text-7xl font-light text-forest leading-[0.95]">
              We don't run a shelter. <em className="italic text-terracotta">We run a sanctuary.</em>
            </h1>
            <p className="text-lg lg:text-xl text-ink/80 max-w-2xl leading-relaxed">
              Pawsomeland Foundation was born out of a simple question — why do we still cage the animals we claim to be saving? Seven years later, we are still answering it. With grass. With sky. With dignity.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-[0_30px_60px_rgba(30,59,39,0.18)]">
              <img src={VOL_IMG} alt="Pawsomeland founder being kissed by a rescued dog" className="w-full h-full object-cover"/>
            </div>
          </div>
        </div>
      </section>

      {/* DROPCAP STORY */}
      <section className="py-20 bg-bone-soft/40">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <SectionLabel className="mb-8 justify-center">The cage-free philosophy</SectionLabel>
          <p className="dropcap font-serif text-xl lg:text-2xl leading-[1.7] text-forest" data-testid="about-philosophy-text">
            Cages were never designed for animals. They were designed for our convenience. They make feeding faster, cleaning easier, paperwork tidier — and they slowly break the spirit of every creature locked inside one. After watching one too many rescue dogs spin endlessly in 4×4 ft kennels, we asked ourselves: what if we just… stopped?
            <br/><br/>
            After years of moving rescued dogs from one temporary place to another, we kept returning to the same question: what would a permanent cage-free sanctuary look like? A place where abandoned, disabled, and forgotten animals could finally live with space, grass, sunlight, and dignity instead of concrete walls and constant uncertainty.
            <br/><br/>
            Cage-free isn't a luxury. It is the bare minimum animals deserve from those who claim to love them.
          </p>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-24 lg:py-32" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="text-center mb-16">
            <SectionLabel className="justify-center mb-5">Milestones</SectionLabel>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-forest">Seven years, one promise</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {TIMELINE.map((t, i) => (
              <div
                key={i}
                className={`bg-white rounded-3xl p-8 border border-bone-soft ${i % 2 === 1 ? "md:translate-y-12" : ""}`}
                data-testid={`timeline-${t.year}`}
              >
                <div className="font-serif text-5xl text-terracotta font-light mb-3">{t.year}</div>
                <h3 className="font-serif text-2xl text-forest mb-3">{t.title}</h3>
                <p className="text-ink/75 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-20 md:mt-28 flex justify-center">
            <Link to="/donate">
              <Button
                data-testid="timeline-donate-cta"
                className="bg-terracotta hover:bg-terracotta-deep text-bone rounded-full h-14 px-9 text-base font-semibold tracking-wide group shadow-[0_15px_40px_rgba(200,90,64,0.45)]"
              >
                Donate &amp; help our cause
                <Heart className="w-4 h-4 ml-2 fill-bone transition-transform group-hover:scale-110" strokeWidth={1.6}/>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="py-24 lg:py-32 bg-forest text-bone" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.25em] text-honey mb-6">
                <span className="w-8 h-px bg-honey" />The team
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl font-light leading-tight">
                The humans <em className="italic text-honey">behind the paws</em>
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-4">
              <p className="text-lg text-bone/80 leading-relaxed">
                A small but mighty team based in Bhopal. Twelve volunteers, three veterinarians, and many hands across the city. Every person here chose this work — most after meeting an animal who refused to give up.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TEAM.map((m, i) => (
              <div key={i} className="group" data-testid={`team-member-${i}`}>
                <div className="aspect-square rounded-3xl overflow-hidden mb-4">
                  <img src={m.img} alt={m.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 grayscale group-hover:grayscale-0"/>
                </div>
                <h3 className="font-serif text-xl">{m.name}</h3>
                <p className="text-sm text-bone/60 uppercase tracking-wider mt-1">{m.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl lg:text-5xl font-light text-forest mb-6">Ready to be part of this?</h2>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/donate"><Button data-testid="about-donate-cta" className="bg-terracotta hover:bg-terracotta-deep text-bone rounded-full h-13 px-8 py-3.5 font-semibold">Donate</Button></Link>
            <Link to="/adopt"><Button data-testid="about-volunteer-cta" variant="outline" className="border-forest text-forest hover:bg-forest hover:text-bone rounded-full h-13 px-8 py-3.5 font-semibold">Volunteer with us</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
